import React, { useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';

const ActivateAccountPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    // Validate password length
    if (formData.password.length < 12) {
      setError('Passwort muss mindestens 12 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`/api/auth/activate/${token}`, {
        password: formData.password,
        username: formData.username || undefined,
      });

      // Auto-login with received token
      login(response.data.access_token, response.data.user);

      setSuccess(true);

      // Redirect to account page after 2 seconds
      setTimeout(() => {
        navigate('/account');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler bei der Account-Aktivierung');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
          <CardContent sx={{ py: 6 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#10b981', mb: 3 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom color="#10b981">
              Konto aktiviert!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Dein Konto wurde erfolgreich aktiviert. Du wirst zur Account-Seite weitergeleitet...
            </Typography>
            <CircularProgress size={40} sx={{ color: '#1e3a5f' }} />
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LockIcon sx={{ fontSize: 60, color: '#e01e26', mb: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Konto aktivieren
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Setzen Sie Ihr Passwort und aktivieren Sie Ihr Konto
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Benutzername (optional ändern)"
              name="username"
              value={formData.username}
              onChange={handleChange}
              helperText="Lassen Sie das Feld leer, um den vorgegebenen Benutzernamen beizubehalten"
              autoComplete="username"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Neues Passwort"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              helperText="Mindestens 12 Zeichen"
              autoComplete="new-password"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Passwort bestätigen"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.5,
                background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
                },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Konto aktivieren'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ActivateAccountPage;
